import React from "react";
import CodeBlock from "@/components/CodeBlock";

const FeatureDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        CLI Command — add feature
      </h2>

      <p className="mb-6">
        The <strong>add feature</strong> command is one of the most powerful
        generators in the AxisNet CLI. It automatically scaffolds a complete
        Feature Module inside the Application layer, including Commands,
        Queries, Validation Rules, Exceptions, Mappings, and optional
        integrations such as Domain entities, Repository implementations,
        Controllers, API versioning, and Unit Tests.
      </p>

      {/* BASIC USAGE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Basic Usage
        </h3>

        <CodeBlock
          language="bash"
          code={`axisnet add feature --name Employee`}
        />

        <p className="mt-4">This command creates a full feature under:</p>

        <CodeBlock
          language="bash"
          code={`/src/{{ProjectName}}.Application/Features/Employees`}
        />
      </div>

      {/* WHAT IS GENERATED */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          What Does a Feature Include?
        </h3>

        <p className="mb-4">
          A Feature in AxisNet follows the official architecture guidelines:
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-6">
          <li>
            <strong>Commands</strong> — Create, Update, Delete operations
          </li>
          <li>
            <strong>Queries</strong> — GetList and GetById handlers
          </li>
          <li>
            <strong>Rules</strong> — Domain validation rules
          </li>
          <li>
            <strong>Exceptions</strong> — Feature-specific error definitions
          </li>
          <li>
            <strong>Mappings</strong> — AutoMapper mapping profiles
          </li>
          <li>
            <strong>README.md</strong> (if <code>generate-docs</code> flag is
            ON)
          </li>
        </ul>

        <h4 className="text-xl font-semibold text-white mb-2">
          Folder Structure
        </h4>

        <CodeBlock
          language="bash"
          code={`Features/
  └── {{DomainName}}s/
      ├── Commands/
      │   ├── Create/
      │   │   ├── Create{{DomainName}}Command.cs
      │   │   ├── Create{{DomainName}}CommandValidator.cs
      │   │   ├── Create{{DomainName}}Handler.cs
      │   │   └── Create{{DomainName}}Response.cs
      │   ├── Update/
      │   │   ├── Update{{DomainName}}Command.cs
      │   │   ├── Update{{DomainName}}CommandValidator.cs
      │   │   ├── Update{{DomainName}}Handler.cs
      │   │   └── Update{{DomainName}}Response.cs
      │   └── Delete/
      │       ├── Delete{{DomainName}}Command.cs
      │       ├── Delete{{DomainName}}CommandValidator.cs
      │       ├── Delete{{DomainName}}Handler.cs
      │       └── Delete{{DomainName}}Response.cs
      ├── Queries/
      │   ├── GetById/
      │   │   ├── Get{{DomainName}}ByIdQuery.cs
      │   │   ├── Get{{DomainName}}ByIdQueryValidator.cs
      │   │   ├── Get{{DomainName}}ByIdHandler.cs
      │   │   └── Get{{DomainName}}ByIdResponse.cs
      │   └── GetList/
      │       ├── Get{{DomainName}}ListQuery.cs
      │       ├── Get{{DomainName}}ListQueryValidator.cs
      │       ├── Get{{DomainName}}ListHandler.cs
      │       └── Get{{DomainName}}ListQueryItemDto.cs
      ├── Rules/
      │   └── {{DomainName}}BusinessRule.cs
      ├── Exceptions/
      │   └── {{DomainName}}NotFoundException.cs
      ├── Mappings/
      │   └── Mapping{{DomainName}}Profile.cs
      └── README.md  # only if generate-docs flag is ON
`}
        />
      </div>

      {/* FLAG INTEGRATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Feature Flags — Optional Auto Generation
        </h3>

        <p className="mb-4">
          The <code>add feature</code> command supports several optional flags
          that allow generating additional parts of the architecture
          automatically.
        </p>

        <ul className="list-disc pl-6 space-y-2 mb-6">
          <li>
            <strong>--domain </strong>
            Automatically creates the Domain entity and registers it if the{" "}
            <code>autofix-dbcontext</code> flag is ON.
          </li>

          <li>
            <strong>--repository </strong>
            Creates both the repository interface and EF Core implementation.
          </li>

          <li>
            <strong>--controller </strong>
            Generates an API controller with CRUD endpoints.
          </li>

          <li>
            <strong>--version &lt;VERSION&gt; </strong>
            Sets the API version for the generated controller.
          </li>

          <li>
            <strong>--test </strong>
            Creates a full test boilerplate for the feature handlers.
          </li>
        </ul>

        <h4 className="text-xl font-semibold text-white mb-3">
          Default Behavior (When Flags Are Not Provided)
        </h4>

        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>--domain</strong> → OFF (no domain created)
          </li>
          <li>
            <strong>--repository</strong> → OFF
          </li>
          <li>
            <strong>--controller</strong> → OFF
          </li>
          <li>
            <strong>--version</strong> → uses template placeholder or default
            1.0 if flag is ON
          </li>
          <li>
            <strong>--test</strong> → OFF
          </li>
        </ul>
      </div>

      {/* HOW IT WORKS (PURE EXPLANATION) */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          How It Works Internally
        </h3>

        <p className="mb-4">
          The <strong>FeatureGenerator</strong> reads all template files located
          under the Feature template directory. It automatically:
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            Replaces placeholders such as <code>{`{{DomainName}}`}</code>
          </li>
          <li>Creates folder paths dynamically</li>
          <li>
            Ensures overwrite protection using the{" "}
            <strong>confirm-overwrite</strong> flag
          </li>
          <li>
            Applies <strong>validate-project-structure</strong> checks
          </li>
          <li>Auto-generates README documentation if enabled</li>
          <li>Executes Git auto-sync with meaningful commit messages</li>
        </ul>

        <p>
          All of this results in a feature module that is consistent, scalable,
          and fully compatible with the Domain-Driven AxisNet architecture.
        </p>
      </div>
    </section>
  );
};

export default FeatureDoc;
